/**
 * A member's card — the app's centre of gravity.
 *
 * The same component renders "me" and anyone else, because they are the same
 * record; only the heading above it changes. Expertise renders as the member's
 * own quoted words rather than as adjectives: the pipeline stores the message
 * ids behind every claim precisely so this screen can show the receipt.
 */

import type { Card } from "../api";
import { Gauge, Strata } from "../components/Strata";
import { Notice, Section, discussions, msgs, num, ruDate, ruMonth } from "../components/common";
import { openChannel } from "../telegram";

interface Props {
  card: Card;
  onOpenThread: (id: number) => void;
  self?: boolean;
  /** Monospace label above the name. The person's name is the heading — a
   *  second display-face title above it would just compete with it. */
  eyebrow: string;
}

export function PersonCard({ card, onOpenThread, self = false, eyebrow }: Props) {
  const { member, stats, profile, expertise, activity, threads, links, channels } = card;
  const name = member.display_name ?? "Участник без имени";
  const months = Object.keys(activity).length;

  return (
    <>
      <div className="hero">
        <div className="rail" style={{ marginBottom: 12 }}>
          {eyebrow}
        </div>
        <h1 className="name">{name}</h1>
        {profile?.headline ? (
          <div className="headline">{profile.headline}</div>
        ) : (
          <div className="headline">
            {member.username ? `@${member.username}` : "Профиль ещё не составлен"}
          </div>
        )}
        <div className="span">
          {ruMonth(stats.first_seen)} — {ruMonth(stats.last_seen)} · {months} мес.
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <b>{num(stats.msg_count)}</b>
          <span>сообщений</span>
        </div>
        <div className="stat">
          <b>{num(stats.threads_started)}</b>
          <span>начал тем</span>
        </div>
        <div className="stat">
          <b>{num(stats.replies_received)}</b>
          <span>ответов ему</span>
        </div>
        <div className="stat">
          <b>{num(stats.reactions_received)}</b>
          <span>реакций</span>
        </div>
      </div>

      <Section title="След в архиве">
        <Strata activity={activity} accent={self} />
      </Section>

      {profile?.bio && (
        <Section title="Кто это">
          <div style={{ padding: "12px 16px 14px", fontSize: 14.5, lineHeight: 1.55 }}>
            {profile.bio}
            {(profile.role || profile.org) && (
              <div className="row-meta" style={{ marginTop: 12 }}>
                {profile.role && <span className="badge">{profile.role}</span>}
                {profile.org && <span className="badge">{profile.org}</span>}
              </div>
            )}
          </div>
        </Section>
      )}

      {expertise.length > 0 && (
        <Section title="Экспертиза — собственными словами">
          {expertise.map((e) => (
            <div key={e.topic_slug} className="claim">
              <div className="claim-head">
                <b>{e.topic_name}</b>
                <Gauge
                  value={e.score * 5}
                  label={`уверенность ${Math.round(e.score * 100)} процентов`}
                />
                <span className="pct">{Math.round(e.score * 100)}%</span>
              </div>
              {e.evidence.map((q) => (
                <blockquote
                  key={q.tg_msg_id}
                  className="quote"
                  onClick={() => onOpenThread(q.thread_root)}
                >
                  {q.text}
                  <time>
                    {ruDate(q.ts)} · #{q.tg_msg_id} → обсуждение
                  </time>
                </blockquote>
              ))}
            </div>
          ))}
        </Section>
      )}

      {profile?.answers_about?.length ? (
        <Section title="Отвечает на вопросы про">
          <div className="chips">
            {profile.answers_about.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {profile?.asks_about?.length ? (
        <Section title="Сам спрашивал про">
          <div className="chips">
            {profile.asks_about.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {channels.length > 0 && (
        <Section title="Каналы">
          {channels.map((c) => (
            <button
              key={c.handle ?? c.title}
              className="row"
              onClick={() => c.handle && openChannel(c.handle)}
            >
              <div className="row-title">{c.title ?? `@${c.handle}`}</div>
              <div className="row-meta">
                {c.handle && <span>@{c.handle}</span>}
                {c.subscribers && (
                  <>
                    <span className="sep">/</span>
                    <span>{num(c.subscribers)} подписчиков</span>
                  </>
                )}
              </div>
            </button>
          ))}
        </Section>
      )}

      {threads.length > 0 && (
        <Section title={`Заметные обсуждения · ${discussions(threads.length)}`}>
          {threads.map((t) => (
            <button key={t.root_msg_id} className="row" onClick={() => onOpenThread(t.root_msg_id)}>
              <div className="row-title">{t.title ?? "Без названия"}</div>
              {t.summary && <div className="row-sub">{t.summary}</div>}
              <div className="row-meta">
                <span>{ruDate(t.started_at)}</span>
                <span className="sep">/</span>
                <span>{msgs(t.msg_count)}</span>
                <span className="sep">/</span>
                <span>{t.participant_count} уч.</span>
              </div>
            </button>
          ))}
        </Section>
      )}

      {links.length > 0 && (
        <Section title="Контакты из самопрезентации">
          {links.map((l) => (
            <div key={`${l.kind}:${l.value}`} className="row" style={{ cursor: "default" }}>
              <div className="row-meta" style={{ marginTop: 0 }}>
                <span className="badge">{l.kind}</span>
                <span>{l.value}</span>
              </div>
            </div>
          ))}
        </Section>
      )}

      {!profile && (
        <Notice>
          Профиль ещё не составлен — не запускался этап обогащения. Всё выше построено
          прямо по архиву.
        </Notice>
      )}
    </>
  );
}
